/**
 * CategoryChips — vertical list of a game's categories (DESIGN frame 03).
 *
 * Despite the legacy file/symbol name, this renders a tappable *list* of category
 * rows (icon · name · chevron). "Все" clears the filter and is shown as the first
 * row; the selected row is highlighted with a gold border. Categories come from the
 * server scoped to ``gameId``; the client performs no filtering of its own.
 */
import { useBranding } from "@melusine/shared-ui";
import { useCategories } from "@entities/category";

import styles from "./CatalogBrowser.module.css";

export interface CategoryChipsProps {
  readonly gameId: number;
  readonly selectedCategoryId: number | undefined;
  readonly onSelect: (categoryId: number | undefined) => void;
}

/**
 * Category glyph — a faceted crystal (дельта §6). Replaces the old hatch placeholders
 * with a consistent gold gem on a dark-indigo, gold-bordered tile. Local SVG, no deps.
 */
const CrystalIcon = (): JSX.Element => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 3h12l3 5-9 13L3 8z" fill="rgba(227,192,124,0.16)" />
    <path d="M3 8h18" />
    <path d="M9 3 7.5 8 12 21" />
    <path d="m15 3 1.5 5L12 21" />
  </svg>
);

/**
 * Per-category glyph. The «Пополнения» category (slug ``topups``) renders its brand
 * asset from object storage (``/api/me`` ``branding.topups``) when configured; any icon
 * uploaded for the category in the admin would take priority, but the storefront
 * ``Category`` payload exposes no such field today, so this is a slug-keyed front-end
 * fallback. Everything else falls back to the generic gold crystal.
 */
function CategoryGlyph({ slug }: { readonly slug: string }): JSX.Element {
  const branding = useBranding();
  const src = slug === "topups" ? branding.topups : "";
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={styles.categoryIconImg}
        draggable={false}
      />
    );
  }
  return <CrystalIcon />;
}

const Chevron = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export function CategoryChips({
  gameId,
  selectedCategoryId,
  onSelect,
}: CategoryChipsProps): JSX.Element | null {
  const { data, isLoading } = useCategories(gameId);

  if (isLoading || !data || data.length === 0) {
    return null;
  }

  return (
    <div className={styles.categoryList} role="tablist" aria-label="Категории">
      <button
        type="button"
        role="tab"
        className={styles.categoryRow}
        data-active={selectedCategoryId === undefined}
        aria-selected={selectedCategoryId === undefined}
        onClick={() => {
          onSelect(undefined);
        }}
      >
        <span className={styles.categoryIcon} aria-hidden="true">
          <CrystalIcon />
        </span>
        <span className={styles.categoryName}>Все услуги</span>
        <span className={styles.categoryChevron} aria-hidden="true">
          <Chevron />
        </span>
      </button>
      {data.map((category) => {
        const active = selectedCategoryId === category.id;
        return (
          <button
            key={category.id}
            type="button"
            role="tab"
            className={styles.categoryRow}
            data-active={active}
            aria-selected={active}
            onClick={() => {
              onSelect(category.id);
            }}
          >
            <span className={styles.categoryIcon} aria-hidden="true">
              <CategoryGlyph slug={category.slug} />
            </span>
            <span className={styles.categoryName}>{category.name}</span>
            <span className={styles.categoryChevron} aria-hidden="true">
              <Chevron />
            </span>
          </button>
        );
      })}
    </div>
  );
}
